import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import SubmitButton from "../submitButton/SubmitButton";
import { sendEmailService } from "../../services/email/services";
import { useNotifications } from "../../context/Notifications";
import { parseEmailHtml } from "../../utils/parse/parse";
import { useTranslator } from "../../context/translator";
import clsx from "clsx";
import {
  useGetCompanyQuery,
  useGetTemplatesQuery,
  useGetUserByIdQuery,
} from "store/api/apiSlice";

function EmailCommunicationModal({ customer, disabled = false }) {
  const { user } = useSelector((state) => state.user);

  const { translate } = useTranslator();

  const { data: userProfile, isLoading: userLoading } = useGetUserByIdQuery(
    user.id,
    {
      skip: !user?.id,
      pollingInterval: 60000,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    },
  );

  const { data: company, isLoading: companyLoading } = useGetCompanyQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const { data: templates, isLoading: templatesLoading } = useGetTemplatesQuery(
    userProfile?.user?.companyId,
    {
      skip: !userProfile?.user?.companyId,
    },
  );

  const [emailTemplates, setEmailTemplates] = useState([]);

  const { addNotification } = useNotifications();

  const [modalOpen, setModalOpen] = useState(false);

  const [template, setTemplate] = useState(emailTemplates[0] || null);

  const [isLoading, setIsLoading] = useState(false);

  const toggleModal = () => {
    if (disabled) return;

    setTemplate(emailTemplates[0] || null);

    setModalOpen((prev) => !prev);
  };

  const handleSendEmail = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    setIsLoading(true);

    try {
      const response = await sendEmailService({
        userId: userProfile?.user?._id,
        templateId: template?._id,
        customerId: customer?._id,
      });

      addNotification({
        id: Date.now(),
        message: response?.sentEmail
          ? translate("email-sent")
          : translate("error-sending-email"),
        kind: response?.sentEmail ? "success" : "danger",
      });

      if (response?.sentEmail) toggleModal();
    } catch (error) {
      console.error(error);

      addNotification({
        id: Date.now(),
        message: translate("error-sending-email"),
        kind: "danger",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onChangeTemplate = (event) => {
    const value = event.target.value;

    const temp = emailTemplates.find((t) => t._id === value);

    setTemplate(temp);
  };

  const emails = customer?.email?.split(/[/]/).filter(Boolean) || [];

  useEffect(() => {
    if (templates && templates?.templates?.length > 0) {
      setEmailTemplates(
        templates?.templates?.filter(
          (t) => t.type === "email" && !t.resetPassword && !t.newUser,
        ),
      );
    }
  }, [templates]);

  if (userLoading || companyLoading || templatesLoading) return null;

  return (
    <>
      {emails.map((splitEmail) => (
        <button
          key={`${customer?._id}-${splitEmail}`}
          onClick={toggleModal}
          disabled={disabled}
          className={clsx(
            "flex items-center text-sm font-medium me-3 transition-colors",
            disabled
              ? "text-gray-400 cursor-not-allowed dark:text-gray-500"
              : "text-blue-600 hover:underline dark:text-blue-400",
          )}
        >
          <span
            className={clsx(
              "flex w-2.5 h-2.5 rounded-full me-1.5 flex-shrink-0",
              disabled ? "bg-slate-300 dark:bg-gray-500" : "bg-blue-600",
            )}
          ></span>

          {splitEmail}
        </button>
      ))}

      {modalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex justify-center items-center bg-black/50 p-4"
        >
          <div className="relative w-full max-w-2xl bg-white dark:bg-gray-700 rounded-lg shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b dark:border-gray-600">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {translate("email-of")} {customer?.fullName}
              </h3>

              <button
                onClick={toggleModal}
                className="text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 p-1.5 rounded-lg"
                aria-label={translate("close")}
              >
                <svg
                  className="w-3 h-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 14 14"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M1 1l6 6m0 0l6 6M7 7l6-6M7 7L1 13"
                  />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="p-5">
              <form onSubmit={handleSendEmail}>
                <div className="grid gap-6 mb-6 md:grid-cols-2">
                  <div>
                    <label
                      htmlFor="template"
                      className="block mb-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                      {translate("select-template")}
                    </label>

                    <select
                      id="template"
                      value={template?._id || ""}
                      onChange={onChangeTemplate}
                      className="block w-full p-2.5 border rounded-lg bg-gray-50 dark:bg-gray-700 text-sm focus:ring-blue-500 focus:border-blue-500 dark:text-white"
                    >
                      {emailTemplates.map((opt) => (
                        <option key={opt._id} value={opt._id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <SubmitButton
                      isLoading={isLoading}
                      textLoading={translate("sending")}
                      text={translate("send")}
                    />
                  </div>
                </div>

                {/* Preview */}
                <div className="mb-6">
                  <span className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
                    {translate("preview")}
                  </span>

                  <div
                    className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800 max-h-64 overflow-auto"
                    dangerouslySetInnerHTML={{
                      __html: `
                        ${template?.headerTemplate?.body || ""}
                        
                        ${parseEmailHtml(
                          customer,
                          userProfile?.user,
                          company,
                          template?.html || "",
                        )}
                        
                        ${template?.footerTemplate?.body || ""}
                      `,
                    }}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EmailCommunicationModal;
